import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { firebaseAuth } from '@/lib/firebase';
import { AppShell } from '@tupai/shell';
import hello from '@tupai/tokens/assets/spot/hello.svg';
import { useTranslation } from '@tupai/i18n/react';
import { firebaseAuthErrorKey } from '@/lib/firebase-auth-errors';

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
});

// Messages are `auth` keys, translated at render.
const schema = z.object({
  email: z.string().min(1, 'errEmailRequired').email('errEmailInvalid'),
});

type FormValues = z.infer<typeof schema>;
type ErrorKey = 'errEmailRequired' | 'errEmailInvalid';

// Same flow as the student app's /forgot-password: Firebase emails a reset
// link to its hosted reset page (our /reset-password is dormant).
function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const emailValue = useWatch({ control, name: 'email' });

  async function onSubmit(data: FormValues) {
    try {
      await firebaseAuth.sendPasswordResetEmail(data.email);
      setSuccess(true);
    } catch (err) {
      const key = firebaseAuthErrorKey(err);
      setError('root', { message: key ? t(key) : t('errSendResetEmail') });
    }
  }

  return (
    <AppShell
      variant="fullscreen"
      tone="surface"
      className="relative items-center justify-center overflow-y-auto px-8 py-8"
    >
      <div className="flex w-full max-w-sm flex-col">
        <div className="mb-8 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-pill bg-bg text-primary">
            <img src={hello} alt="" className="w-12 h-12" style={{ objectFit: 'contain' }} />
          </div>
        </div>
        <h1 className="mb-8 font-display text-2xl text-content text-center">
          {t('resetPasswordTitle')}
        </h1>

        {success ? (
          <div className="text-center">
            <p className="mb-6 text-sm text-content">
              {t('resetLinkSentBefore')} <strong>{emailValue}</strong>.
            </p>
            <button onClick={() => navigate({ to: '/login' })} className="cta-primary w-full">
              {t('backToLogin')}
            </button>
          </div>
        ) : (
          <form className="flex flex-col gap-5" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label className="text-sm font-bold text-muted tracking-wide mb-1.5 block">
                {t('emailAddressLabel')}
              </label>
              <input
                type="email"
                {...register('email')}
                className="h-12 w-full rounded-md border-[1.5px] border-border bg-surface px-4 py-3 text-content placeholder-hint outline-none focus:border-primary transition-colors"
                placeholder={t('emailPlaceholder')}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-error">{t(errors.email.message as ErrorKey)}</p>
              )}
            </div>
            {errors.root && <p className="text-sm text-error">{errors.root.message}</p>}
            <div className="mt-2 flex flex-col gap-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="cta-primary disabled:opacity-40"
              >
                {isSubmitting ? t('sending') : t('sendResetLink')}
              </button>
              <button
                type="button"
                className="text-sm font-bold text-muted hover:text-primary transition-colors mt-2"
                onClick={() => navigate({ to: '/login' })}
              >
                {t('backToLogin')}
              </button>
            </div>
          </form>
        )}
      </div>
    </AppShell>
  );
}
